/**
 * Direct Duplication (no clipboard serialization)
 * 
 * Deep-clones tasks and their nested sprints by JSON-cloning the original
 * data and remapping all IDs in one pass. Uses the same proven pattern as
 * templateCopy.js — no lossy serialize/deserialize roundtrip.
 */

import { remapConditionTaskIds } from '../../../shared/ConditionsDropdown/utils/conditionHelpers';

function newId(prefix = 'task') {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Deep-clone a nested sprint, giving every task/option/subtask/etc a new ID
 * while remapping all internal references (dependsOn, unlockConditions, branches).
 */
export function deepCloneNestedSprintDirect(original, parentTaskId) {
  if (!original) return null;

  const cloned = JSON.parse(JSON.stringify(original));
  const newSprintId = newId('nested-sprint');
  const now = new Date().toISOString();

  const taskIdMap = {};
  const optionIdMap = {};

  // Pass 1: assign new IDs to every task and decision option
  const tasks = (cloned.tasks || []).map(task => {
    const newTaskId = newId('task');
    taskIdMap[task.id] = newTaskId;

    task.id = newTaskId;

    // Subtasks, deliverables, inputs — new IDs, keep everything else
    if (Array.isArray(task.subtasks)) {
      task.subtasks = task.subtasks.map(s => ({ ...s, id: newId('subtask') }));
    }
    if (Array.isArray(task.deliverables)) {
      task.deliverables = task.deliverables.map(d => ({ ...d, id: newId('deliverable') }));
    }
    if (Array.isArray(task.inputs)) {
      task.inputs = task.inputs.map(i => ({ ...i, id: newId('input') }));
    }

    // Decision options — new IDs, build remap
    if (task.nodeType === 'decision' && Array.isArray(task.options)) {
      task.options = task.options.map(opt => {
        const newOptId = newId('option');
        optionIdMap[opt.id] = newOptId;
        return { ...opt, id: newOptId };
      });
    }

    return task;
  });

  // Pass 2: remap all references
  tasks.forEach(task => {
    // Milestone host attachment — host is always within the same sprint
    if (task.milestoneAnchor?.hostTaskId && taskIdMap[task.milestoneAnchor.hostTaskId]) {
      task.milestoneAnchor = { ...task.milestoneAnchor, hostTaskId: taskIdMap[task.milestoneAnchor.hostTaskId] };
    }
    if (task.noteAnchor?.hostTaskId && taskIdMap[task.noteAnchor.hostTaskId]) {
      task.noteAnchor = { ...task.noteAnchor, hostTaskId: taskIdMap[task.noteAnchor.hostTaskId] };
    }

    // dependsOn
    if (Array.isArray(task.dependsOn) && task.dependsOn.length > 0) {
      task.dependsOn = task.dependsOn.map(dep => {
        if (typeof dep === 'string') {
          return taskIdMap[dep] || dep;
        }
        if (dep && dep.taskId) {
          return {
            ...dep,
            taskId: taskIdMap[dep.taskId] || dep.taskId,
            ...(dep.branch && optionIdMap[dep.branch] ? { branch: optionIdMap[dep.branch] } : {}),
          };
        }
        return dep;
      }).filter(Boolean);
    }

    // unlockConditions (v1 and v2 handled by remapConditionTaskIds)
    if (task.unlockConditions) {
      task.unlockConditions = remapConditionTaskIds(task.unlockConditions, taskIdMap);
      task.unlockConditions = remapBranches(task.unlockConditions, optionIdMap);
    }

    // decision answer / selectedOption
    if (task.answer && optionIdMap[task.answer]) {
      task.answer = optionIdMap[task.answer];
    }
    if (task.selectedOption && optionIdMap[task.selectedOption]) {
      task.selectedOption = optionIdMap[task.selectedOption];
    }
  });

  return {
    ...cloned,
    id: newSprintId,
    parentTaskId,
    parentSprintId: null, // caller sets on root; tree clone sets for descendants
    createdAt: now,
    updatedAt: now,
    tasks,
    // Stale childOrder still points at the *source* nested campaign IDs.
    // Keeping it can make originals show under the clone in the sidebar.
    // Remapped after the full tree is cloned (see deepCloneNestedSprintTree).
    childOrder: undefined,
  };
}

/**
 * Deep-clone a nested sprint and all nested sprints inside its tasks.
 * @returns {{ sprint: Object, descendants: Array }}
 */
export function deepCloneNestedSprintTree(
  original,
  parentTaskId,
  parentSprintId,
  nestedSprints,
  allSprints,
  nestedSprintOverrides,
  sprintIdMap = null,
  pendingChildOrders = null,
) {
  const idMap = sprintIdMap || new Map();
  const childOrders = pendingChildOrders || new Map(); // newSprintId -> original childOrder
  const isRootCall = !sprintIdMap;

  const oldSprintId = original?.id;
  const cloned = deepCloneNestedSprintDirect(original, parentTaskId);
  if (!cloned) return { sprint: null, descendants: [] };

  if (oldSprintId) {
    idMap.set(oldSprintId, cloned.id);
  }
  if (Array.isArray(original?.childOrder) && original.childOrder.length > 0) {
    childOrders.set(cloned.id, original.childOrder);
  }

  if (parentSprintId) {
    cloned.parentSprintId = parentSprintId;
  }

  const descendants = [];

  (cloned.tasks || []).forEach(task => {
    if (!task.nestedSprintId) return;
    const childOriginal = resolveNestedSprintForDuplicate(
      task.nestedSprintId,
      nestedSprints,
      allSprints,
      nestedSprintOverrides,
    );
    if (!childOriginal) {
      task.nestedSprintId = undefined;
      return;
    }

    const { sprint: childSprint, descendants: childDescendants } = deepCloneNestedSprintTree(
      childOriginal,
      task.id,
      cloned.id,
      nestedSprints,
      allSprints,
      nestedSprintOverrides,
      idMap,
      childOrders,
    );
    if (childSprint) {
      task.nestedSprintId = childSprint.id;
      descendants.push(childSprint, ...childDescendants);
    } else {
      task.nestedSprintId = undefined;
    }
  });

  // Remap sidebar order onto newly cloned nested IDs (never keep source IDs).
  if (isRootCall) {
    const byNewId = new Map([[cloned.id, cloned], ...descendants.map((s) => [s.id, s])]);
    for (const [newId, oldOrder] of childOrders) {
      const sprint = byNewId.get(newId);
      if (!sprint) continue;
      const remapped = oldOrder.map((id) => idMap.get(id)).filter(Boolean);
      sprint.childOrder = remapped.length > 0 ? remapped : undefined;
    }
  }

  return { sprint: cloned, descendants };
}

function remapBranches(conditions, optionIdMap) {
  if (!conditions || Object.keys(optionIdMap).length === 0) return conditions;

  if (conditions.version === 2 && conditions.blocks) {
    return {
      ...conditions,
      blocks: conditions.blocks.map(block => ({
        ...block,
        conditions: block.conditions.map(c =>
          c.branch && optionIdMap[c.branch] ? { ...c, branch: optionIdMap[c.branch] } : c
        ),
      })),
    };
  }

  if (Array.isArray(conditions)) {
    return conditions.map(c =>
      c && c.branch && optionIdMap[c.branch] ? { ...c, branch: optionIdMap[c.branch] } : c
    );
  }

  return conditions;
}

function resolveNestedSprintForDuplicate(nestedSprintId, nestedSprints, allSprints, nestedSprintOverrides) {
  if (nestedSprintOverrides?.has(nestedSprintId)) {
    return nestedSprintOverrides.get(nestedSprintId);
  }
  const fromNested = nestedSprints.find(ns => ns.id === nestedSprintId);
  const fromAll = (allSprints || []).find(s => s.id === nestedSprintId);
  const aOk = fromNested && Array.isArray(fromNested.tasks);
  const bOk = fromAll && Array.isArray(fromAll.tasks);
  if (aOk && !bOk) return fromNested;
  if (bOk && !aOk) return fromAll;
  return fromNested || fromAll;
}

/**
 * Duplicate one or more tasks directly (no clipboard).
 * Returns { tasks, positions, nestedSprints } ready for onPasteTasks.
 * @param {Map} nestedSprintOverrides - optional id -> full sprint from loadFullSprint
 */
export function duplicateTasksDirect(taskIds, allTasks, nodePositions, nestedSprints, allSprints = [], nestedSprintOverrides = null) {
  const tasksToDuplicate = taskIds
    .map(id => allTasks.find(t => t.id === id))
    .filter(Boolean);

  if (tasksToDuplicate.length === 0) return null;

  const now = new Date().toISOString();

  const clonedTasks = [];
  const positions = {};
  const clonedNestedSprints = [];

  // Build ID maps for remapping inter-task references within the duplicated set
  const taskIdMap = {};
  const optionIdMap = {};

  // Pass 1: clone tasks with new IDs
  tasksToDuplicate.forEach(task => {
    const clone = JSON.parse(JSON.stringify(task));
    const newTaskId = newId('task');
    taskIdMap[task.id] = newTaskId;

    clone.id = newTaskId;
    clone.name = tasksToDuplicate.length === 1 ? `${task.name} (copy)` : task.name;
    clone.createdAt = now;
    clone.updatedAt = now;
    clone.order = (allTasks.length || 0) + clonedTasks.length + 1;

    // New IDs for nested arrays
    if (Array.isArray(clone.subtasks)) {
      clone.subtasks = clone.subtasks.map(s => ({ ...s, id: newId('subtask') }));
    }
    if (Array.isArray(clone.deliverables)) {
      clone.deliverables = clone.deliverables.map(d => ({ ...d, id: newId('deliverable') }));
    }
    if (Array.isArray(clone.inputs)) {
      clone.inputs = clone.inputs.map(i => ({ ...i, id: newId('input') }));
    }

    if (clone.nodeType === 'decision' && Array.isArray(clone.options)) {
      clone.options = clone.options.map(opt => {
        const nid = newId('option');
        optionIdMap[opt.id] = nid;
        return { ...opt, id: nid };
      });
      if (clone.answer && optionIdMap[clone.answer]) clone.answer = optionIdMap[clone.answer];
      if (clone.selectedOption && optionIdMap[clone.selectedOption]) clone.selectedOption = optionIdMap[clone.selectedOption];
    }

    delete clone.x;
    delete clone.y;

    clonedTasks.push(clone);
  });

  // Pass 2: remap references between duplicated tasks
  clonedTasks.forEach(task => {
    // Milestone host attachment: if the host was duplicated too, re-anchor to the
    // copy; otherwise keep anchoring to the original host (same rule as dependsOn)
    if (task.milestoneAnchor?.hostTaskId && taskIdMap[task.milestoneAnchor.hostTaskId]) {
      task.milestoneAnchor = { ...task.milestoneAnchor, hostTaskId: taskIdMap[task.milestoneAnchor.hostTaskId] };
    }
    if (task.noteAnchor?.hostTaskId && taskIdMap[task.noteAnchor.hostTaskId]) {
      task.noteAnchor = { ...task.noteAnchor, hostTaskId: taskIdMap[task.noteAnchor.hostTaskId] };
    }

    if (Array.isArray(task.dependsOn) && task.dependsOn.length > 0) {
      task.dependsOn = task.dependsOn.map(dep => {
        if (typeof dep === 'string') {
          return taskIdMap[dep] || dep;
        }
        if (dep && dep.taskId) {
          return {
            ...dep,
            taskId: taskIdMap[dep.taskId] || dep.taskId,
            ...(dep.branch && optionIdMap[dep.branch] ? { branch: optionIdMap[dep.branch] } : {}),
          };
        }
        return dep;
      }).filter(Boolean);
    } else {
      task.dependsOn = [];
    }

    if (task.unlockConditions) {
      task.unlockConditions = remapConditionTaskIds(task.unlockConditions, taskIdMap);
      task.unlockConditions = remapBranches(task.unlockConditions, optionIdMap);
    } else {
      task.unlockConditions = [];
    }
  });

  // Pass 3: clone nested sprints (all depths)
  clonedTasks.forEach(task => {
    if (!task.nestedSprintId) return;
    const originalSprint = resolveNestedSprintForDuplicate(
      task.nestedSprintId,
      nestedSprints,
      allSprints,
      nestedSprintOverrides,
    );
    if (!originalSprint) {
      task.nestedSprintId = undefined;
      return;
    }
    const { sprint: clonedSprint, descendants } = deepCloneNestedSprintTree(
      originalSprint,
      task.id,
      null,
      nestedSprints,
      allSprints,
      nestedSprintOverrides,
    );
    if (clonedSprint) {
      clonedNestedSprints.push(clonedSprint, ...descendants);
      task.nestedSprintId = clonedSprint.id;
    } else {
      task.nestedSprintId = undefined;
    }
  });

  return { tasks: clonedTasks, positions, nestedSprints: clonedNestedSprints };
}

/**
 * Ensures nested sprint graphs are loaded before direct duplicate (same issue as clipboard copy).
 */
export async function duplicateTasksDirectWithLoad(taskIds, allTasks, nodePositions, nestedSprints, allSprints, loadFullSprint) {
  const nestedSprintOverrides = new Map();
  if (typeof loadFullSprint === 'function') {
    const idsToLoad = new Set();
    const queue = taskIds
      .map(id => allTasks.find(t => t.id === id))
      .filter(Boolean)
      .map(t => t.nestedSprintId)
      .filter(Boolean);

    while (queue.length > 0) {
      const nsId = queue.shift();
      if (!nsId || idsToLoad.has(nsId)) continue;
      idsToLoad.add(nsId);

      let sprint = nestedSprintOverrides.get(nsId)
        || resolveNestedSprintForDuplicate(nsId, nestedSprints, allSprints, nestedSprintOverrides);
      if (sprint && !Array.isArray(sprint.tasks)) {
        const loaded = await loadFullSprint(nsId);
        if (loaded) {
          nestedSprintOverrides.set(nsId, loaded);
          sprint = loaded;
        }
      }
      (sprint?.tasks || []).forEach(t => {
        if (t.nestedSprintId) queue.push(t.nestedSprintId);
      });
    }

    for (const nsId of idsToLoad) {
      if (nestedSprintOverrides.has(nsId)) continue;
      const sprint = resolveNestedSprintForDuplicate(nsId, nestedSprints, allSprints, nestedSprintOverrides);
      if (sprint && !Array.isArray(sprint.tasks)) {
        const loaded = await loadFullSprint(nsId);
        if (loaded) nestedSprintOverrides.set(nsId, loaded);
      }
    }
  }
  return duplicateTasksDirect(taskIds, allTasks, nodePositions, nestedSprints, allSprints, nestedSprintOverrides);
}
